import { makeWhitespaceVisible, normalizeUniqueKey, workPackageCodeKey, workPackageNameKey } from "./uniqueKey";

/**
 * Renders a backend uniqueness refusal (409, CS-6 payload) with the client's own wording (CS-8).
 * The backend sends a reason code and the stored conflicting value; the prose lives in the
 * `uniqueness.*` locale keys. An unknown code renders the generic fallback, never the raw code.
 */
export const UNIQUENESS_REASON_CODES = [
  "PROJECT_NAME_DUPLICATE_IN_PORTFOLIO",
  "PROJECT_CODE_DUPLICATE_IN_PORTFOLIO",
  "SUB_CODE_DUPLICATE_IN_PROJECT",
  "SUB_CODE_DUPLICATE_IN_SUBMISSION",
  "SUB_CODE_RENAME_NOT_SUPPORTED",
  "SUB_CODE_LEGACY_COLLISION",
  "WORK_PACKAGE_CODE_DUPLICATE_IN_PROJECT",
  "WORK_PACKAGE_CODE_DUPLICATE_IN_PORTFOLIO",
  "WORK_PACKAGE_EFFECTIVE_KEY_DUPLICATE",
  "WORK_PACKAGE_CODE_CHANGES_IDENTITY",
  "VALUE_DIFFERS_BY_WHITESPACE_ONLY",
  "RESOURCE_NAME_DUPLICATE_IN_PORTFOLIO",
  "RESOURCE_CODE_DUPLICATE_IN_PORTFOLIO",
];

export const isUniquenessRefusal = (error) =>
  error?.response?.status === 409 && typeof error?.response?.data?.reasonCode === "string";

/** The refusal payload of an axios error, or null. */
export const uniquenessRefusalOf = (error) => (isUniquenessRefusal(error) ? error.response.data : null);

const stripSpaces = (v) => String(v ?? "").replace(/[\s ]+/g, "");

/** Two values are "the same apart from whitespace" when removing every space makes them equal. */
export const differsByWhitespaceOnly = (submitted, conflictsWith) =>
  submitted != null &&
  conflictsWith != null &&
  submitted !== conflictsWith &&
  stripSpaces(submitted) === stripSpaces(conflictsWith);

/** Same key, but not byte-identical: the pre-submit checks use this to decide whether to refuse. */
export const collides = (submitted, existing) =>
  existing != null && normalizeUniqueKey(submitted) === normalizeUniqueKey(existing);

/**
 * @param t i18next t
 * @param refusal {reasonCode, field, submitted, conflictsWith, rowIndex, context, args}
 * @returns the localised sentence(s); the whitespace clarifier is appended when it applies
 */
export function refusalMessage(t, refusal) {
  if (!refusal) return "";
  const { reasonCode, submitted, conflictsWith, rowIndex, context = {}, args = {} } = refusal;
  const known = UNIQUENESS_REASON_CODES.includes(reasonCode);
  const params = {
    submitted,
    conflictsWith,
    rowIndex,
    ...context,
    ...args,
    tabSuffix: ["stock", "nonLabor"].includes(args.tabSuffix) ? t(`uniqueness.tab.${args.tabSuffix}`) : "",
    conflictField: ["code", "name"].includes(args.conflictField) ? t(`uniqueness.field.${args.conflictField}`) : args.conflictField || "",
    interpolation: { escapeValue: false },
  };
  let text = known ? t(`uniqueness.${reasonCode}`, params) : t("uniqueness.UNKNOWN", params);
  if (reasonCode !== "VALUE_DIFFERS_BY_WHITESPACE_ONLY" && differsByWhitespaceOnly(submitted, conflictsWith)) {
    text +=
      " " +
      t("uniqueness.VALUE_DIFFERS_BY_WHITESPACE_ONLY", {
        submitted,
        conflictsWith,
        submittedVisible: makeWhitespaceVisible(submitted),
        conflictsWithVisible: makeWhitespaceVisible(conflictsWith),
        interpolation: { escapeValue: false },
      });
  }
  return text;
}

/**
 * First collision of a submitted {name, code} against a collection the client already holds
 * (feedback only — the server stays authoritative). Returns {field, conflictsWith, item} or null.
 */
export function findCollision(items, { name, code }, { excludeId, nameOf = (i) => i?.name, codeOf = (i) => i?.code } = {}) {
  for (const item of items || []) {
    if (!item) continue;
    if (excludeId !== undefined && excludeId !== null && item.id === excludeId) continue;
    if (name !== undefined && name !== null && name !== "" && collides(name, nameOf(item))) {
      return { field: "name", conflictsWith: nameOf(item), item };
    }
    if (code !== undefined && code !== null && code !== "" && collides(code, codeOf(item))) {
      return { field: "code", conflictsWith: codeOf(item), item };
    }
  }
  return null;
}

/** Turns an axios error into the `{field: message}` shape the forms render inline, or null. */
export function inlineRefusalOf(t, error) {
  const refusal = uniquenessRefusalOf(error);
  if (!refusal) return null;
  const message = refusalMessage(t, refusal);
  return { [refusal.field || "name"]: message, message, reasonCode: refusal.reasonCode };
}

/**
 * Work packages: first collision within one submission on either join axis — the NAME axis
 * (buckets, getName() fallback) or the CODE axis (usages, getCode() fallback). Mirrors the
 * backend's WorkPackageUniquenessGuard. Rows are `{name, code}`; indices are 1-based positions.
 */
export function findWorkPackageCollision(rows) {
  const blank = (v) => v === null || v === undefined || v === "";
  const accepted = [];
  for (let i = 0; i < (rows || []).length; i++) {
    const row = rows[i];
    if (!row || (blank(row.name) && blank(row.code))) continue;
    const nameKey = workPackageNameKey(row.code, row.name);
    const codeKey = workPackageCodeKey(row.code, row.name) || null;
    for (const [j, other] of accepted) {
      if (nameKey && nameKey === workPackageNameKey(other.code, other.name)) {
        const bothRealNames = !blank(row.name) && !blank(other.name);
        return {
          rowIndex: i + 1,
          otherRowIndex: j + 1,
          field: blank(row.name) ? "code" : "name",
          reasonCode: bothRealNames ? "SUB_CODE_DUPLICATE_IN_SUBMISSION" : "WORK_PACKAGE_EFFECTIVE_KEY_DUPLICATE",
          submitted: blank(row.name) ? row.code : row.name,
          conflictsWith: blank(other.name) ? other.code : other.name,
          conflictField: blank(other.name) ? "code" : "name",
        };
      }
      if (codeKey && codeKey === (workPackageCodeKey(other.code, other.name) || null)) {
        const bothRealCodes = !blank(row.code) && !blank(other.code);
        return {
          rowIndex: i + 1,
          otherRowIndex: j + 1,
          field: blank(row.code) ? "name" : "code",
          reasonCode: bothRealCodes ? "SUB_CODE_DUPLICATE_IN_SUBMISSION" : "WORK_PACKAGE_EFFECTIVE_KEY_DUPLICATE",
          submitted: blank(row.code) ? row.name : row.code,
          conflictsWith: blank(other.code) ? other.name : other.code,
          conflictField: blank(other.code) ? "name" : "code",
        };
      }
    }
    accepted.push([i, row]);
  }
  return null;
}

/**
 * `state.resource.mappedResources` holds wrappers `{ managers, mappedGenericWithSpecifics:
 * { parent, childs } }` — flatten to the actual resources, collaborators excluded (the backend
 * exempts them: every user carries the code USER).
 */
export const flattenMappedResources = (mappedResources) =>
  (mappedResources || [])
    .flatMap((w) => [w?.mappedGenericWithSpecifics?.parent, ...(w?.mappedGenericWithSpecifics?.childs || [])])
    .filter(Boolean)
    .filter((r) => r.entityType !== "COLLABORATOR");

/** Which tab a resource lives on — mirrors the backend's ResourceUniquenessGuard.tabOf. */
export const resourceTabOf = (resource) => {
  if (!resource) return null;
  if (resource.dimension === "STOCK") return "stock";
  if (resource.entityType === "GENERIC_NONLABOR" || resource.entityType === "SPECIFIC_NONLABOR") return "nonLabor";
  return null;
};

/** Builds the refusal a pre-submit check shows (same shape as the server's, so one renderer). */
export const localRefusal = (reasonCode, field, submitted, conflictsWith, extra = {}) => ({
  reasonCode,
  field,
  submitted,
  conflictsWith,
  ...extra,
});
